import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { fetchAccessMe } from './api'

/**
 * Права текущего пользователя (эпик ACCESS, `q.access.me`): по ним гард роутов и меню
 * решают, что показывать кастомной роли. Обновляются при каждом (пере)подключении
 * шины — смена роли рвёт соединение (kick), UI переподключается и перечитывает права.
 *
 * Пока права не загружены, гард смотрит на роли из JWT (прежнее поведение).
 */
export const useAccessStore = defineStore('access', () => {
  const loaded = ref(false)
  const superuser = ref(false)
  const capabilities = ref<ReadonlySet<string>>(new Set())
  const roles = ref<string[]>([])
  /** Коды ролей, которыми пользователь управляет (O26): правит их и раздаёт. */
  const managesRoles = ref<ReadonlySet<string>>(new Set())

  /**
   * Ядро без `q.access.me` (UI выкатили раньше Core: 503 / no responders) — права не
   * спрашиваем повторно на каждом переходе, гард работает по ролям JWT.
   */
  const unsupported = ref(false)

  /** Один запрос на всех: гард первого перехода и переподключение шины не дублируют его. */
  let inFlight: Promise<void> | null = null
  /** Растёт на reset: ответ, запрошенный до сброса (прежний пользователь), не записывается. */
  let epoch = 0

  const isNoResponders = (err: unknown): boolean =>
    /^503$|no responders/i.test(err instanceof Error ? err.message : String(err))

  /**
   * Перечитать права. Ошибка сообщается ровно один раз — здесь, в месте запроса:
   * переподнимается отдельным промисом, её покажет глобальный перехватчик
   * (boot/globalErrorHandler). Ждущие (гард, boot) получают уже обработанный промис,
   * иначе одна ошибка давала бы по уведомлению на каждого ждущего.
   */
  const refresh = (): Promise<void> => {
    if (inFlight) return inFlight
    const startedAt = epoch
    const request: Promise<void> = fetchAccessMe()
      .then((me) => {
        if (startedAt !== epoch) return // ответ для прежнего пользователя (был reset)
        superuser.value = me.superuser
        capabilities.value = new Set(me.capabilities)
        roles.value = me.roles
        managesRoles.value = new Set(me.managesRoles)
        loaded.value = true
        unsupported.value = false
      })
      .catch((err: unknown) => {
        if (startedAt !== epoch) return
        if (isNoResponders(err)) {
          unsupported.value = true
          console.warn('q.access.me: ядро не отвечает (старее UI?) — меню и гард по ролям JWT', err)
          return
        }
        void Promise.reject(err)
      })
      .finally(() => {
        if (inFlight === request) inFlight = null
      })
    inFlight = request
    return request
  }

  /**
   * Дождаться первой загрузки, но не дольше `timeoutMs`: гард не должен вешать
   * навигацию, если шина ещё не поднялась, — тогда он отработает по ролям JWT.
   */
  const ensureLoaded = async (timeoutMs = 3000): Promise<void> => {
    if (loaded.value || unsupported.value) return
    await Promise.race([refresh(), new Promise<void>((resolve) => setTimeout(resolve, timeoutMs))])
  }

  const can = (capability: string): boolean => superuser.value || capabilities.value.has(capability)

  /** Управляет ли пользователь ролью (O26): может её править и выдавать. */
  const manages = (code: string): boolean => superuser.value || managesRoles.value.has(code)

  const reset = () => {
    epoch += 1
    inFlight = null
    loaded.value = false
    unsupported.value = false
    superuser.value = false
    capabilities.value = new Set()
    roles.value = []
    managesRoles.value = new Set()
  }

  return {
    loaded,
    unsupported: computed(() => unsupported.value),
    superuser,
    capabilities: computed(() => capabilities.value),
    roles,
    refresh,
    ensureLoaded,
    can,
    manages,
    reset,
  }
})
